import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Divider, Grid, Paper, Tab, Tabs } from '@mui/material';
import AppLayout from '../../layout/app.layout';
import { viewDataById } from '../../services/distributor.service';
import ROUTE_PATH from '../../config/routes.config';
import Loading from '../../components/table-elements/loading.component';
import SnackbarInfo from '../../components/common/snackbar-info.component';
import PageNotFoundView from '../errors/not-found.view';
import ViewGridData from '../../components/table-elements/view-grid-data.component';
import useAccess from '../../hooks/access.hook';
import Iconify from '../../components/common/iconify.component';
import replaceIdUrl from '../../utils/url.util';
import {
  VIEW_ADDRESS_COLUMNS,
  VIEW_OTHER_COLUMNS,
  VIEW_PAYMENT_COLUMNS,
  VIEW_PROFILE_COLUMNS,
} from '../../config/module-configs/distributor.config';

const ContentOwnerViewDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasOperationAccess } = useAccess();

  const TABS_CONFIG = [
    {
      value: 'contentOwnerBasicInfo',
      label: 'Basic Info',
    },
  ];
  const [tabValue, setTabValue] = useState(TABS_CONFIG[0].value);
  const [loading, setLoading] = useState(true);
  const [showPageNotFound, setShowPageNotFound] = useState(false);
  const [distributorDetail, setDistributorDetail] = useState({});
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  useEffect(() => {
    viewDataById(id)
      .then((res) => {
        setDistributorDetail(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          setShowPageNotFound(true);
          return;
        }
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [id]);

  return Number.isNaN(Number(id)) || showPageNotFound ? (
    <PageNotFoundView />
  ) : (
    <AppLayout
      pageTitle={`Distributor Details - ${distributorDetail.email}`}
      showBack
      handleBack={() => navigate(ROUTE_PATH.CONTENT_OWNERS)}
    >
      {hasOperationAccess('distributors', 'edit') && (
        <Box display="flex" justifyContent="flex-end" my={1}>
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="bx:edit" />}
            onClick={() => {
              const url = replaceIdUrl(ROUTE_PATH.CONTENT_OWNERS_EDIT, id);
              navigate(url);
            }}
            disabled={loading}
          >
            Edit Distributor
          </Button>
        </Box>
      )}

      <Box height="100%" display="flex">
        <Tabs
          value={tabValue}
          aria-label="tabs"
          orientation="vertical"
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="transparent"
          sx={{ minWidth: 150, maxWidth: 150 }}
          onChange={(_, v) => setTabValue(v)}
        >
          {TABS_CONFIG.map((tab, index) => (
            <Tab
              key={index + 1}
              value={tab.value}
              label={tab.label}
              sx={{
                background: tabValue === tab.value && '#fff',
                borderLeft: tabValue === tab.value && '2px solid #FD0100',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
            />
          ))}
        </Tabs>

        <Box flexGrow={1}>
          {loading && <Loading />}

          {!loading && (
            <Box component={Paper} p={1.5}>
              <Grid item xs={12} sm={12} md={12}>
                <Divider
                  textAlign="center"
                  variant="middle"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Profile
                </Divider>
              </Grid>
              <ViewGridData
                columns={VIEW_PROFILE_COLUMNS}
                row={distributorDetail}
              />

              <Grid item xs={12} sm={12} md={12}>
                <Divider
                  textAlign="center"
                  variant="middle"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Address
                </Divider>
              </Grid>
              <ViewGridData
                columns={VIEW_ADDRESS_COLUMNS}
                row={distributorDetail}
              />

              <Grid item xs={12} sm={12} md={12}>
                <Divider
                  textAlign="center"
                  variant="middle"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Payment
                </Divider>
              </Grid>
              <ViewGridData
                columns={VIEW_PAYMENT_COLUMNS}
                row={distributorDetail}
              />

              <Grid item xs={12} sm={12} md={12}>
                <Divider
                  textAlign="center"
                  variant="middle"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Other
                </Divider>
              </Grid>
              <ViewGridData
                columns={VIEW_OTHER_COLUMNS}
                row={distributorDetail}
              />
            </Box>
          )}
        </Box>
      </Box>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </AppLayout>
  );
};

export default ContentOwnerViewDetails;
